/**
 * Cross-page selection + select-all for infinite-scroll tables: only a window of
 * the result set is ever loaded, so "select all" can't be expressed as a set of
 * loaded ids, and TanStack's loaded-model-only helpers (`getIsAllRowsSelected`)
 * must never be the source of truth for "all matching".
 *
 * A discriminated union keeps the two modes' id-sets from ever being confused:
 *   - `loaded` (inclusion): header select-all selects LOADED rows only;
 *     `selectedIds` is the exact chosen set — TanStack's `rowSelection` verbatim,
 *     so the engine holds it with no translation.
 *   - `allMatching` (exclusion): a distinct banner action means "every matching
 *     row except `deselectedIds`", so a page scrolled in later renders checked
 *     without being enumerated and the engine never holds a key per unloaded row.
 *
 * `loadedCount` must be the post-dedup logical count, not a raw page length, or a
 * duplicate id emitted twice inflates "all loaded rows selected". The bulk
 * payload derives from selection STATE, never the current-page row model (R11);
 * the exclusion wire format is left to the consuming migration — the one seam.
 */
import { useMemo } from 'react'
import type { RowData, RowSelectionState, Table } from '@tanstack/react-table'

/** The selection semantics a table is operating under. */
export type SelectionMode = 'loaded' | 'allMatching'

/**
 * Cross-page selection state, keyed by a stable row id. A discriminated union so
 * the inclusion set (`selectedIds`) and exclusion set (`deselectedIds`) can never
 * be confused — each mode carries only its own set.
 */
export type SelectionModel =
  | {
      mode: 'loaded'
      /** Chosen row ids (inclusion) — TanStack's `rowSelection` shape verbatim. */
      selectedIds: RowSelectionState
    }
  | {
      mode: 'allMatching'
      /** Ids de-selected after "select all N matching"; effective = all − these. */
      deselectedIds: RowSelectionState
    }

export const EMPTY_SELECTION: SelectionModel = { mode: 'loaded', selectedIds: {} }

/** Whether the model has at least one selected row. */
export const hasSelection = (model: SelectionModel, serverTotal?: number): boolean =>
  getSelectedCount(model, serverTotal) > 0

/** Selected-row count; an unknowable `allMatching` count collapses to `0`. */
export const getSelectedCount = (model: SelectionModel, serverTotal?: number): number => {
  const exact = getSelectedCountExact(model, serverTotal)

  return exact ?? 0
}

/**
 * Exact selected count, or `undefined` when unknowable (an `allMatching`
 * selection whose `serverTotal` hasn't resolved) — distinct from
 * {@link getSelectedCount} so a banner shows "All matching selected", not `0`.
 */
export const getSelectedCountExact = (
  model: SelectionModel,
  serverTotal?: number,
): number | undefined => {
  if (model.mode === 'loaded') {
    return countTrue(model.selectedIds)
  }

  if (serverTotal === undefined) {
    return undefined
  }

  return Math.max(0, serverTotal - countTrue(model.deselectedIds))
}

const countTrue = (state: RowSelectionState): number => {
  let count = 0
  for (const value of Object.values(state)) {
    if (value) {
      count += 1
    }
  }

  return count
}

export type HeaderSelectionState = {
  /**
   * `true` only when the selection provably covers the ENTIRE result set. Under
   * `hasNextPage` a loaded-inclusion selection is never `checked`, however many
   * loaded rows are ticked, so the header never implies rows it cannot see.
   */
  checked: boolean
  /** Some-but-not-all selected. Mutually exclusive with `checked`. */
  indeterminate: boolean
  selectedCount: number
  /**
   * Offer the "select all N matching" banner: the loaded page is fully selected
   * but the result set is larger than what is loaded. Does not itself switch mode.
   */
  canSelectAllMatching: boolean
}

export type DeriveHeaderSelectionParams = {
  model: SelectionModel
  /** Post-dedup loaded row count — a raw page length would double-count dup ids. */
  loadedCount: number
  serverTotal?: number
  hasNextPage?: boolean
}

/**
 * Header checkbox state from selection STATE, never TanStack's loaded-model-only
 * `getIsAllRowsSelected` (which would report `checked` after selecting the loaded
 * page and mislead under infinite scroll). Invariant: `checked` iff the selection
 * covers the whole result set; anything short of that is `indeterminate`.
 */
export const deriveHeaderSelection = (
  params: DeriveHeaderSelectionParams,
): HeaderSelectionState => {
  const { model, loadedCount, serverTotal, hasNextPage } = params
  const selectedCount = getSelectedCount(model, serverTotal)

  // Whole result set known-loaded: nothing more to fetch, and either the total is
  // unknown (loaded set IS the whole set) or the total is matched by loadedCount.
  const resultSetFullyLoaded =
    hasNextPage !== true && (serverTotal === undefined || serverTotal <= loadedCount)

  if (model.mode === 'allMatching') {
    const deselectedCount = countTrue(model.deselectedIds)

    return {
      checked: deselectedCount === 0,
      indeterminate: deselectedCount > 0,
      selectedCount,
      canSelectAllMatching: false,
    }
  }

  const nothingSelected = selectedCount === 0
  const allLoadedSelected = loadedCount > 0 && selectedCount >= loadedCount

  // Checked requires the whole set loaded AND every loaded row selected; under
  // `hasNextPage` that is impossible, so the header stays indeterminate.
  const checked = allLoadedSelected && resultSetFullyLoaded

  return {
    checked,
    indeterminate: !nothingSelected && !checked,
    selectedCount,
    canSelectAllMatching: allLoadedSelected && !resultSetFullyLoaded,
  }
}

/**
 * Bulk-action intent derived from selection STATE, never the current-page row
 * model, so ids selected on unloaded pages are never dropped. A discriminated
 * union: `ids` (exact list, the safe default) vs `allMatchingExcept` (exclusion,
 * whose wire format the consuming migration decides — this module only names it).
 */
export type BulkSelection =
  | { kind: 'ids'; ids: string[] }
  | { kind: 'allMatchingExcept'; excludedIds: string[] }

export const deriveBulkSelection = (model: SelectionModel): BulkSelection => {
  if (model.mode === 'loaded') {
    return { kind: 'ids', ids: selectedKeys(model.selectedIds) }
  }

  return { kind: 'allMatchingExcept', excludedIds: selectedKeys(model.deselectedIds) }
}

const selectedKeys = (state: RowSelectionState): string[] => {
  const keys: string[] = []
  for (const [id, value] of Object.entries(state)) {
    if (value) {
      keys.push(id)
    }
  }

  return keys
}

/**
 * Effective per-row selected state for a LOADED row. In `allMatching` mode a row
 * is selected UNLESS deselected, so a row scrolled in after "select all N
 * matching" renders checked, not unchecked.
 */
export const isRowSelected = (model: SelectionModel, id: string): boolean => {
  if (model.mode === 'loaded') {
    return model.selectedIds[id] === true
  }

  return model.deselectedIds[id] !== true
}

/** Toggle one loaded row, returning the NEXT model (immutable — never mutates). */
export const toggleRow = (model: SelectionModel, id: string, selected: boolean): SelectionModel => {
  if (model.mode === 'loaded') {
    return { mode: 'loaded', selectedIds: withKey(model.selectedIds, id, selected) }
  }

  // allMatching set is EXCLUSIONS: selecting removes the id, de-selecting adds it.
  return { mode: 'allMatching', deselectedIds: withKey(model.deselectedIds, id, !selected) }
}

/**
 * Set-or-clear a key, returning a NEW object. An absent key means "not a member":
 * clearing OMITS it (never stores `false`) so counts never see stale entries.
 */
const withKey = (state: RowSelectionState, id: string, present: boolean): RowSelectionState => {
  if (present) {
    return { ...state, [id]: true }
  }
  if (state[id] === undefined) {
    return state
  }
  const { [id]: _removed, ...rest } = state

  return rest
}

/**
 * Header select-all in the DEFAULT sense: select every LOADED row, stay in
 * `loaded` mode. Distinct from {@link selectAllMatching}, the banner action.
 */
export const selectAllLoaded = (loadedIds: string[]): SelectionModel => {
  const selectedIds: RowSelectionState = {}
  for (const id of loadedIds) {
    selectedIds[id] = true
  }

  return { mode: 'loaded', selectedIds }
}

export const clearSelection = (): SelectionModel => EMPTY_SELECTION

/** Switch to the exclusion model — "select all N matching", nothing excluded. */
export const selectAllMatching = (): SelectionModel => ({ mode: 'allMatching', deselectedIds: {} })

/**
 * TanStack `rowSelection` for the ENGINE, built over LOADED ids only so it stays
 * O(loaded) and the engine never holds keys for unloaded rows. In `allMatching`
 * mode, every loaded id EXCEPT the deselected ones; keeps `row.getIsSelected()`
 * in agreement with {@link isRowSelected}. Fed into `state.rowSelection` by the
 * preset (see `data-table.tsx`).
 */
export const deriveLoadedRowSelection = (
  model: SelectionModel,
  loadedIds: string[],
): RowSelectionState => {
  if (model.mode === 'loaded') {
    return model.selectedIds
  }

  const selection: RowSelectionState = {}
  for (const id of loadedIds) {
    if (model.deselectedIds[id] !== true) {
      selection[id] = true
    }
  }

  return selection
}

/** Loaded row ids off the live table, deduped by TanStack's stable `row.id`. */
export const getLoadedRowIds = <TData extends RowData>(table: Table<TData>): string[] =>
  table.getRowModel().rows.map((row) => row.id)

export type UseTableSelectionOptions = {
  model: SelectionModel
  /** Post-dedup loaded count (not a raw page length). */
  loadedCount: number
  serverTotal?: number
  hasNextPage?: boolean
}

export type UseTableSelectionResult = {
  header: HeaderSelectionState
  bulkSelection: BulkSelection
  selectedCount: number
  /** `undefined` when an `allMatching` count cannot be known (unresolved total). */
  selectedCountExact: number | undefined
}

/**
 * Bundle header + bulk + count derivations behind one memo so consumers get
 * stable identities under React Compiler. Pure — the state transitions
 * (`toggleRow`/`selectAll*`/`clearSelection`) stay the consumer's reducers.
 */
export const useTableSelection = (options: UseTableSelectionOptions): UseTableSelectionResult => {
  const { model, loadedCount, serverTotal, hasNextPage } = options

  return useMemo<UseTableSelectionResult>(
    () => ({
      header: deriveHeaderSelection({ model, loadedCount, serverTotal, hasNextPage }),
      bulkSelection: deriveBulkSelection(model),
      selectedCount: getSelectedCount(model, serverTotal),
      selectedCountExact: getSelectedCountExact(model, serverTotal),
    }),
    [model, loadedCount, serverTotal, hasNextPage],
  )
}
